import axios from 'axios';
import url from '../url.js';
import dayjs from 'dayjs';

const chatService = {
  // 創建與夥伴的聊天活動和小組
  createPartnerChat: async (partner) => {
    try {
      const userId = localStorage.getItem('userId');
      const jwtToken = localStorage.getItem('jwtToken');
      const headers = jwtToken ? { Authorization: `Bearer ${jwtToken}` } : {};

      // 1. 創建活動
      const activityData = {
        userId: userId,
        title: `與 ${partner.name} 的對話`,
        startDate: dayjs().toISOString(),
        endDate: dayjs().add(3, 'month').toISOString() // 3個月有效期
      };

      const activityResponse = await axios.post(
        `${url.backendHost}api/activities/create`, 
        activityData, 
        { headers, withCredentials: true }
      );
      
      const newActivity = activityResponse.data;
      
      // 1.5. 刷新用戶權限以包含新活動
      
      const renewResponse = await axios.get(`${url.backendHost}api/users/renew`, { headers, withCredentials: true });
      
      // 更新 JWT token
      const newToken = renewResponse.data.jwtToken;
      if (newToken) {
        
        localStorage.setItem('jwtToken', newToken);
        headers.Authorization = `Bearer ${newToken}`;
      } else {
        console.warn('警告：無法獲取新的 JWT token');
      }
      
      // 2. 創建小組
      const groupData = {
        groupName: `${partner.name} 聊天室`,
        activityId: newActivity.id,
        numGroups: 1
      };

      // 嘗試創建小組，如果權限失敗則重試一次
      let groupResponse;
      try {
        
        groupResponse = await axios.post(
          `${url.backendHost}api/activities/createGroups`, 
          groupData, 
          { headers, withCredentials: true }
        );
      } catch (groupError) {
        if (groupError.response?.status === 403) {
          
          // 再次刷新權限
          const retryRenewResponse = await axios.get(`${url.backendHost}api/users/renew`, { headers, withCredentials: true });
          const retryToken = retryRenewResponse.data.jwtToken;
          if (retryToken) {
            localStorage.setItem('jwtToken', retryToken);
            headers.Authorization = `Bearer ${retryToken}`;
          }
          
          // 重試創建小組
          
          groupResponse = await axios.post(
            `${url.backendHost}api/activities/createGroups`, 
            groupData, 
            { headers, withCredentials: true }
          );
        } else {
          throw groupError;
        }
      }
      
      const group = groupResponse.data.groups[0];
      const joinCode = group.joinCode;

      // 3. 用戶加入小組
      const joinData = { userId: userId };
      await axios.put(
        `${url.backendHost}api/groups/${joinCode}/join`, 
        joinData, 
        { headers, withCredentials: true }
      );

      // 4. 初始化活動設定為聊天室模式
      try {
        await chatService.initializeChatSettings(newActivity.id);
        
      } catch (settingsError) {
        console.error('❌ 聊天室設定初始化失敗:', settingsError);
        // 嘗試基本的強制設定
        try {
          
          await axios.post(
            url.backendHost + 'api/activityInfo/',
            {
              activityId: newActivity.id,
              userId: userId,
              settings: { chatmode: 'chatroom', isPartnerChat: true }
            },
            { headers, withCredentials: true }
          );
          
        } catch (basicError) {
          console.error('❌ 基本設定也失敗:', basicError);
          throw new Error('無法初始化聊天室設定，請稍後再試');
        }
      }

      // 5. 設置夥伴到活動中
      try {
        await chatService.setupPartnerInActivity(newActivity.id, group.id, partner, headers);
        
      } catch (partnerError) {
        console.warn('⚠️ 第一次設置夥伴失敗，嘗試重試:', partnerError);
        // 如果是權限問題，再次刷新 token 並重試
        if (partnerError.response?.status === 403) {
          try {
            
            const retryRenewResponse = await axios.get(`${url.backendHost}api/users/renew`, { headers, withCredentials: true });
            const retryToken = retryRenewResponse.data.jwtToken;
            if (retryToken) {
              localStorage.setItem('jwtToken', retryToken);
              headers.Authorization = `Bearer ${retryToken}`;
            }
            
            // 重試設置夥伴
            await chatService.setupPartnerInActivity(newActivity.id, group.id, partner, headers);
            
          } catch (retryError) {
            console.error('❌ 重試設置夥伴也失敗:', retryError);
            alert(`警告：夥伴設置失敗，您可能需要手動添加夥伴到活動中。錯誤：${retryError.message}`);
          }
        } else {
          console.error('❌ 設置夥伴失敗:', partnerError);
          alert(`警告：夥伴設置失敗，您可能需要手動添加夥伴到活動中。錯誤：${partnerError.message}`);
        }
      }

      // 6. 更新 token
      try {
        await axios.get(`${url.backendHost}api/users/renew`, { withCredentials: true });
      } catch (err) {
        console.warn('更新 token 失敗:', err);
      }

      return {
        activity: newActivity,
        group: group,
        joinCode: joinCode
      };

    } catch (error) {
      console.error('創建夥伴聊天失敗:', error);
      
      // 提供更詳細的錯誤信息
      let errorMessage = '創建聊天室失敗，請稍後再試';
      if (error.response?.status === 403) {
        errorMessage = '權限不足，請重新登入後再試';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data) {
        errorMessage = error.response.data;
      }
      
      throw new Error(errorMessage);
    }
  },

  // 初始化聊天設定
  initializeChatSettings: async (activityId) => {
    try {
      
      
      // 設定為聊天室模式
      const chatSettings = {
        chatmode: 'chatroom',
        allowedModes: ['chatroom'],
        isPartnerChat: true
      };

      // 先嘗試獲取現有設定
      let activityInfoResponse;
      try {
        activityInfoResponse = await axios.get(
          url.backendHost + 'api/activityInfo/' + activityId,
          { withCredentials: true }
        );
        
        
        // 更新現有設定
        await axios.put(
          url.backendHost + 'api/activityInfo/' + activityId,
          {
            activityId: activityId,
            userId: localStorage.getItem('userId'),
            settings: chatSettings,
          },
          { withCredentials: true }
        );
      } catch (err) {
        // 如果不存在，創建新的並直接設定正確的 settings
        
        activityInfoResponse = await axios.post(
          url.backendHost + 'api/activityInfo/',
          {
            activityId: activityId,
            userId: localStorage.getItem('userId'),
            settings: chatSettings, // 直接在創建時設定正確的 settings
          },
          { withCredentials: true }
        );
      }

      
      return chatSettings;
    } catch (error) {
      console.error('初始化聊天設定失敗:', error);
      throw error;
    }
    },

  // 設置夥伴到活動中
  setupPartnerInActivity: async (activityId, groupId, partner, headers) => {
    try {
      
      
      const partnerData = {
        activityId: activityId,
        groupId: groupId, // 可以為 null 表示全域夥伴
        partnerId: partner.id,
        partnerName: partner.name,
        partnerConfig: {
          partnerName: partner.name,
          roleTask: partner.roleTask,
          knowledgeBase: partner.knowledgeBase,
          communicationStyle: partner.communicationStyle,
          responseTimeline: partner.responseTimeline,
          description: partner.description,
          category: partner.category,
          tags: partner.tags
        }
      };

      const response = await axios.post(
        url.backendHost + 'api/activities/setupPartner',
        partnerData,
        { headers, withCredentials: true }
      );

      
      return response.data;
    } catch (error) {
      console.error('設置夥伴失敗:', error);
      throw error;
    }
  },

  // 獲取活動中的夥伴列表
  getActivityPartners: async (activityId) => {
    try {
      const response = await axios.get(
        url.backendHost + `api/activities/${activityId}/partners`,
        { withCredentials: true }
      );
      return response.data.partners;
    } catch (error) {
      console.error('獲取活動夥伴失敗:', error);
      throw error;
    }
  },

  // 從活動中移除夥伴
  removePartnerFromActivity: async (activityId, partnerId) => {
    try {
      const response = await axios.delete(
        url.backendHost + `api/activities/${activityId}/partners/${partnerId}`,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('移除夥伴失敗:', error);
      throw error;
    }
  },

  // 導航到聊天室
  navigateToChat: (activity, group) => {
    // 設置必要的 localStorage 和 sessionStorage 項目
    localStorage.setItem('activityId', activity.id);
    sessionStorage.setItem('activityId', activity.id);
    localStorage.setItem('groupId', group.id);
    sessionStorage.setItem('groupId', group.id);
    if (group.joinCode) {
      localStorage.setItem('joinCode', group.joinCode);
      sessionStorage.setItem('joinCode', group.joinCode);
    }

    // 使用 window.location.reload 來刷新頁面並觸發新活動的載入
    // 這樣主頁面會重新載入活動列表，包含新創建的夥伴聊天活動
    window.location.reload();
  }
};

export default chatService; 